import { type RefObject, useLayoutEffect, useState } from "react";

import {
    measureSkeletonBlocks,
    SKELETON_HEIGHT_ATTRIBUTE,
    SKELETON_IGNORE_ATTRIBUTE,
    SKELETON_NO_CHILDREN_ATTRIBUTE,
    SKELETON_WIDTH_ATTRIBUTE,
} from "./measureSkeletonBlocks";
import { isSameBlocks, type SkeletonBlock } from "./skeletonLayout";

/** Attributes that can move or resize blocks without resizing the content root. */
const LAYOUT_ATTRIBUTES = [
    "class",
    "style",
    "hidden",
    "src",
    "srcset",
    "width",
    "height",
    "open",
    SKELETON_IGNORE_ATTRIBUTE,
    SKELETON_NO_CHILDREN_ATTRIBUTE,
    SKELETON_WIDTH_ATTRIBUTE,
    SKELETON_HEIGHT_ATTRIBUTE,
];

type UseSkeletonBlocksOptions = {
    contentRef: RefObject<HTMLElement | null>;
    overlayRef: RefObject<HTMLElement | null>;
    isEnabled: boolean;
    fallbackRadius: number;
};

/**
 * Measures the placeholder content into skeleton blocks, positioned relative to
 * the overlay, and keeps them in sync with resizes, DOM changes, late-loading
 * media, and web fonts while enabled. While disabled, the last blocks are kept.
 */
export function useSkeletonBlocks({
    contentRef,
    overlayRef,
    isEnabled,
    fallbackRadius,
}: UseSkeletonBlocksOptions): SkeletonBlock[] | null {
    const [blocks, setBlocks] = useState<SkeletonBlock[] | null>(null);

    useLayoutEffect(
        function observeContentLayout() {
            const content = contentRef.current;
            const overlay = overlayRef.current;

            if (!isEnabled || !content || !overlay) {
                return;
            }

            let frame = 0;

            const measure = () => {
                frame = 0;
                const nextBlocks = measureSkeletonBlocks(
                    content,
                    overlay.getBoundingClientRect(),
                    { fallbackRadius },
                );

                setBlocks((current) =>
                    isSameBlocks(current, nextBlocks) ? current : nextBlocks,
                );
            };

            const scheduleMeasure = () => {
                if (frame === 0) {
                    frame = requestAnimationFrame(measure);
                }
            };

            measure();

            const resizeObserver = new ResizeObserver(scheduleMeasure);
            resizeObserver.observe(content);

            const mutationObserver = new MutationObserver(scheduleMeasure);
            mutationObserver.observe(content, {
                attributeFilter: LAYOUT_ATTRIBUTES,
                characterData: true,
                childList: true,
                subtree: true,
            });

            content.addEventListener("load", scheduleMeasure, true);
            // A font swap can re-wrap text without any DOM change or content resize.
            document.fonts.addEventListener("loadingdone", scheduleMeasure);

            return () => {
                cancelAnimationFrame(frame);
                resizeObserver.disconnect();
                mutationObserver.disconnect();
                content.removeEventListener("load", scheduleMeasure, true);
                document.fonts.removeEventListener("loadingdone", scheduleMeasure);
            };
        },
        [contentRef, overlayRef, isEnabled, fallbackRadius],
    );

    return blocks;
}
